import { computed, ComputedRef } from 'vue';

import i18n from '@/plugins/vue-i18n.js';
import { useEventBus } from '@/hooks/useEventBus.ts';
import { type ContentLanguage, type Locale } from '@/locales/supportedLocales.ts';
import type { Choice, Locale as VueI18nLocale, NamedValue, Path } from 'vue-i18n';

export type { ContentLanguage, Locale } from '@/locales/supportedLocales.ts';
type TranslationValues = Choice | NamedValue | unknown[];

interface I18n {
    isFrLocale: ComputedRef<boolean>;
    isEnLocale: ComputedRef<boolean>;
    locale: ComputedRef<Locale>;
    contentLanguage: ComputedRef<ContentLanguage>;
    t(key: Path, values?: TranslationValues, localeOverride?: VueI18nLocale): string;
    te(key: Path, locale?: VueI18nLocale): boolean;
    tc(key: Path, choice?: Choice, values?: TranslationValues): string;
    toggleLocale: () => void;
    n(value: number, key?: Path, locale?: VueI18nLocale): string;
}

export const useI18n = (): I18n => {
    const { emit } = useEventBus();

    const locale = computed(() => i18n.getLocale() as Locale);
    const contentLanguage = computed(() => i18n.getContentLanguage());

    return {
        locale,
        contentLanguage,
        isFrLocale: computed(() => contentLanguage.value === 'fr'),
        isEnLocale: computed(() => contentLanguage.value === 'en'),
        t: (key: Path, values?: TranslationValues, localeOverride?: VueI18nLocale): string => {
            if (localeOverride) {
                if (typeof localeOverride !== 'string') {
                    if (import.meta.env.DEV) {
                        console.warn(
                            `[useI18n] t("${key}") third argument must be a locale string, got ${typeof localeOverride}.` +
                                ' For pluralization, pass a number as the second argument.'
                        );
                    }

                    return String(i18n.t(key, values));
                }

                return String(i18n.t(key, values ?? {}, { locale: localeOverride }));
            }

            return String(i18n.t(key, values));
        },
        te: (key: Path, locale?: VueI18nLocale): boolean => i18n.te(key, locale),
        tc: (key: Path, choice?: Choice, values?: TranslationValues): string =>
            String(
                i18n.t(
                    key,
                    Array.isArray(values)
                        ? values
                        : { ...(values as NamedValue), count: choice ?? 1 },
                    choice ?? 1
                )
            ),
        toggleLocale: (): void =>
            emit('set-locale', contentLanguage.value === 'fr' ? 'en-CA' : 'fr-CA'),
        n: (value: number, key?: Path, locale?: VueI18nLocale): string =>
            String(i18n.n(value, key, locale)),
    };
};
